// Thin typed fetch wrapper for the trellis-app REST API.
//
// All task mutations live under /api/workspaces/[workspaceId]/tasks/... and
// share the same response envelopes, defined in src/lib/tasks/helpers.ts:
//
//   success → { data: T, meta?: Record<string, unknown> }    (status 200/201)
//   error   → { error: string, code?: string }               (status 4xx/5xx)
//
// Auth is the user's Supabase session cookie — same-origin fetch carries it
// automatically, but we set credentials: 'same-origin' explicitly to make the
// intent visible.
//
// NOTE on `as T`: this file contains the only `as` casts in the codebase
// (a `return undefined as T` for 204 responses and `return payload.data as T`
// at the JSON boundary). They are intentional and approved: `request<T>` is a
// generic JSON unwrapper, the runtime payload is `unknown`, and bridging
// unknown→T without runtime schemas (Zod) requires a single boundary cast.
// The casts are isolated here so every callsite stays cast-free; do not copy
// this pattern elsewhere.

import { buildApiErrorMessage, extractApiErrorCode } from '@/lib/api/error-message'

interface ApiErrorOptions extends ErrorOptions {
  code?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined

  constructor(message: string, status: number, codeOrOptions?: string | ApiErrorOptions) {
    super(message, typeof codeOrOptions === 'string' ? undefined : codeOrOptions)
    this.name = 'ApiError'
    this.status = status
    this.code = typeof codeOrOptions === 'string' ? codeOrOptions : codeOrOptions?.code
  }
}

function apiErrorWithCause(
  message: string,
  status: number,
  cause: unknown,
  code?: string
): ApiError {
  const error = new ApiError(message, status, code)
  error.cause = cause
  return error
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

interface RequestOptions {
  body?: unknown
  headers?: Readonly<Record<string, string>>
  method?: Method
  signal?: AbortSignal
}

interface SuccessBody {
  data: unknown
  hasMore?: unknown
  meta?: unknown
  nextOffset?: unknown
}

function isSuccessBody(value: unknown): value is SuccessBody {
  return typeof value === 'object' && value !== null && 'data' in value
}

// Internal: issue the request and return the parsed envelope.
async function requestInternal(
  path: string,
  options: RequestOptions
): Promise<
  | {
      data: unknown
      meta?: Record<string, unknown>
      hasMore: boolean
      nextOffset: number | null
    }
  | undefined
> {
  const { method = 'GET', body, signal, headers } = options

  let response: Response
  try {
    response = await fetch(path, {
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      headers: body === undefined ? headers : { ...headers, 'Content-Type': 'application/json' },
      method,
      signal,
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network request failed'
    throw apiErrorWithCause(message, 0, err)
  }

  // 204 No Content — DELETE handlers occasionally use this; treat as void.
  if (response.status === 204) {
    return undefined
  }

  let payload: unknown
  try {
    payload = await response.json()
  } catch (error) {
    if (!response.ok) {
      throw apiErrorWithCause(
        buildApiErrorMessage(null, 'Request failed', response.status),
        response.status,
        error,
        extractApiErrorCode(null, response.status)
      )
    }
    throw apiErrorWithCause('Invalid JSON in response', response.status, error)
  }

  if (!response.ok) {
    throw new ApiError(
      buildApiErrorMessage(payload, 'Request failed', response.status),
      response.status,
      extractApiErrorCode(payload, response.status)
    )
  }

  if (!isSuccessBody(payload)) {
    throw new ApiError('Malformed response: missing "data" field', response.status)
  }

  const meta =
    typeof payload.meta === 'object' && payload.meta !== null
      ? (payload.meta as Record<string, unknown>)
      : undefined

  return {
    data: payload.data,
    hasMore: payload.hasMore === true,
    meta,
    nextOffset: typeof payload.nextOffset === 'number' ? payload.nextOffset : null,
  }
}

/**
 * Issue a JSON request to the trellis-app REST API and unwrap the `data` field.
 *
 * - Serializes `body` as JSON when present.
 * - On 2xx, expects `{ data: T }` and returns `T`. `meta` is discarded.
 * - On non-2xx, throws `ApiError` with the server's `error` message and `code`.
 * - On network failure, throws `ApiError(message, 0)`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const result = await requestInternal(path, options)
  if (!result) {
    return undefined as T
  }
  return result.data as T
}

/**
 * Same as `request<T>` but returns the full `{ data, meta }` envelope.
 *
 * Use when callers need to read `meta` (e.g. PATCH /visits responses carry
 * `meta.merged` to signal an auto-merge happened, plus `droppedSecondaries`
 * counts that the UI surfaces as toast counters).
 */
export async function requestEnvelope<T>(
  path: string,
  options: RequestOptions = {}
): Promise<{ data: T; meta?: Record<string, unknown> }> {
  const result = await requestInternal(path, options)
  if (!result) {
    return { data: undefined as T, meta: undefined }
  }
  return { data: result.data as T, meta: result.meta }
}

export async function requestPage<T>(
  path: string,
  options: RequestOptions = {}
): Promise<{ data: T[]; hasMore: boolean; nextOffset: number | null }> {
  const result = await requestInternal(path, options)
  if (!(result && Array.isArray(result.data))) {
    throw new ApiError('Malformed response: expected paginated data', 200)
  }

  return {
    data: result.data as T[],
    hasMore: result.hasMore,
    nextOffset: result.nextOffset,
  }
}
